import React, { useMemo, useState } from 'react';
import { Input, Select, Table, Tag } from 'antd';

import AgeDisplay, {
  AGE_STATUS_OPTIONS,
  ageStatus,
} from '@totem/components/common/dateDisplay/AgeDisplay';
import {
  DashboardBuilding,
  DashboardOrg,
  DashboardTicket,
} from '@totem/components/ticketing/ticketDashboard/types';
import TagDisplay, {
  TICKET_PRIORITY_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';

type Props = {
  tickets: DashboardTicket[];
  orgs: Record<string, DashboardOrg>;
  buildings: Record<string, DashboardBuilding>;
};

const TICKET_URL_BASE = 'https://dashboard.ib-services.net/dashboard/ticket/';

const filterOption = (
  input: string,
  option?: { label: string; value: string },
) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const FindingsTab = ({ tickets, orgs, buildings }: Props) => {
  const [priorityFilter, setPriorityFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [selectedAgeStatuses, setSelectedAgeStatuses] = useState<string[]>([]);

  const customerOptions = useMemo(
    () =>
      [...new Set(tickets.map((tkt) => tkt.o).filter(Boolean))]
        .map((oid) => ({ value: oid, label: orgs[oid]?.name ?? oid }))
        .sort((aOpt, bOpt) => aOpt.label.localeCompare(bOpt.label)),
    [tickets, orgs],
  );

  const buildingOptions = useMemo(
    () =>
      [...new Set(tickets.map((tkt) => tkt.b).filter(Boolean))]
        .map((bid) => ({ value: bid, label: buildings[bid]?.name ?? bid }))
        .sort((aOpt, bOpt) => aOpt.label.localeCompare(bOpt.label)),
    [tickets, buildings],
  );

  const categoryOptions = useMemo(
    () =>
      [...new Set(tickets.map((tkt) => tkt.cat).filter(Boolean))]
        .sort()
        .map((cat) => ({ value: cat, label: cat })),
    [tickets],
  );

  const deviceOptions = useMemo(
    () =>
      [...new Set(tickets.map((tkt) => tkt.dev).filter(Boolean))]
        .sort()
        .map((dev) => ({ value: dev, label: dev })),
    [tickets],
  );

  const ownerOptions = useMemo(
    () =>
      [...new Set(tickets.map((tkt) => tkt.an || tkt.q || '').filter(Boolean))]
        .sort()
        .map((own) => ({ value: own, label: own })),
    [tickets],
  );

  const filtered = useMemo(() => {
    let rows = tickets.slice();
    if (priorityFilter) rows = rows.filter((tkt) => tkt.p === priorityFilter);
    if (flagFilter === 'br') rows = rows.filter((tkt) => tkt.br === 1);
    else if (flagFilter === 'r24') rows = rows.filter((tkt) => tkt.r24 === 1);
    else if (flagFilter === 'un') rows = rows.filter((tkt) => tkt.ha === 0);
    else if (flagFilter === 'pr') rows = rows.filter((tkt) => tkt.pr === 1);
    else if (flagFilter === 'def') rows = rows.filter((tkt) => tkt.def === 1);
    if (selectedCustomers.length > 0)
      rows = rows.filter((tkt) => selectedCustomers.includes(tkt.o));
    if (selectedBuildings.length > 0)
      rows = rows.filter((tkt) => selectedBuildings.includes(tkt.b));
    if (selectedCategories.length > 0)
      rows = rows.filter((tkt) => selectedCategories.includes(tkt.cat));
    if (selectedDevices.length > 0)
      rows = rows.filter((tkt) => selectedDevices.includes(tkt.dev));
    if (selectedOwners.length > 0)
      rows = rows.filter(
        (tkt) =>
          selectedOwners.includes(tkt.an) || selectedOwners.includes(tkt.q),
      );
    if (selectedAgeStatuses.length > 0)
      rows = rows.filter((tkt) =>
        selectedAgeStatuses.includes(ageStatus(tkt.ageD)),
      );
    if (search) {
      const query = search.toLowerCase();
      rows = rows.filter(
        (tkt) =>
          tkt.sum.toLowerCase().includes(query) ||
          tkt.num.toLowerCase().includes(query) ||
          (orgs[tkt.o]?.name ?? '').toLowerCase().includes(query),
      );
    }
    return rows.sort((rowA, rowB) => rowB.u - rowA.u);
  }, [
    tickets,
    priorityFilter,
    flagFilter,
    selectedCustomers,
    selectedBuildings,
    selectedCategories,
    selectedDevices,
    selectedOwners,
    selectedAgeStatuses,
    search,
    orgs,
  ]);

  const columns = [
    {
      title: 'Pri',
      dataIndex: 'p',
      key: 'p',
      width: 60,
      render: (val: string) => (
        <TagDisplay value={val} config={TICKET_PRIORITY_STYLES} />
      ),
    },
    {
      title: 'Ticket',
      key: 'ticket',
      width: 100,
      render: (_: unknown, ticket: DashboardTicket) => (
        <div>
          <a
            href={`${TICKET_URL_BASE}${ticket.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {ticket.num}
          </a>
          <div style={{ fontSize: 10, color: '#94A3B8' }}>{ticket.st}</div>
        </div>
      ),
    },
    {
      title: 'Summary',
      key: 'summary',
      render: (_: unknown, ticket: DashboardTicket) => ticket.sum,
    },
    {
      title: 'Customer',
      key: 'org',
      width: 150,
      sorter: (rowA: DashboardTicket, rowB: DashboardTicket) =>
        (orgs[rowA.o]?.name ?? rowA.o).localeCompare(
          orgs[rowB.o]?.name ?? rowB.o,
        ),
      render: (_: unknown, ticket: DashboardTicket) =>
        orgs[ticket.o]?.name ?? ticket.o,
    },
    {
      title: 'Building',
      key: 'building',
      width: 150,
      sorter: (rowA: DashboardTicket, rowB: DashboardTicket) =>
        (buildings[rowA.b]?.name ?? '').localeCompare(
          buildings[rowB.b]?.name ?? '',
        ),
      render: (_: unknown, ticket: DashboardTicket) =>
        buildings[ticket.b]?.name ?? '',
    },
    {
      title: 'Category',
      dataIndex: 'cat',
      key: 'cat',
      width: 130,
      sorter: (rowA: DashboardTicket, rowB: DashboardTicket) =>
        (rowA.cat ?? '').localeCompare(rowB.cat ?? ''),
    },
    {
      title: 'Device',
      dataIndex: 'dev',
      key: 'dev',
      width: 120,
      sorter: (rowA: DashboardTicket, rowB: DashboardTicket) =>
        (rowA.dev ?? '').localeCompare(rowB.dev ?? ''),
    },
    {
      title: 'Owner',
      key: 'owner',
      width: 140,
      sorter: (rowA: DashboardTicket, rowB: DashboardTicket) =>
        (rowA.an || rowA.q || '').localeCompare(rowB.an || rowB.q || ''),
      render: (_: unknown, ticket: DashboardTicket) =>
        ticket.an || ticket.q || '',
    },
    {
      title: 'Age (d)',
      dataIndex: 'ageD',
      key: 'ageD',
      width: 80,
      align: 'right' as const,
      sorter: (rowA: DashboardTicket, rowB: DashboardTicket) =>
        rowA.ageD - rowB.ageD,
      render: (_: unknown, ticket: DashboardTicket) => (
        <AgeDisplay date={ticket.cAt} />
      ),
    },
    {
      title: 'Stale (d)',
      dataIndex: 'stlD',
      key: 'stlD',
      width: 64,
      align: 'right' as const,
      sorter: (rowA: DashboardTicket, rowB: DashboardTicket) =>
        rowA.stlD - rowB.stlD,
      render: (val: number) => (
        <span
          style={{
            fontWeight: val > 14 ? 700 : 400,
            color: val > 30 ? '#9C2A2A' : val > 14 ? '#C88A2B' : undefined,
          }}
        >
          {val.toFixed(1)}
        </span>
      ),
    },
    {
      title: 'Flags',
      key: 'flags',
      width: 160,
      render: (_: unknown, ticket: DashboardTicket) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {ticket.br === 1 && <Tag color="error">PAST SLA</Tag>}
          {ticket.r24 === 1 && ticket.br === 0 && (
            <Tag color="warning">@ RISK</Tag>
          )}
          {ticket.ha === 0 && <Tag color="orange">UNOWNED</Tag>}
          {ticket.pr === 1 && <Tag color="success">PROACTIVE</Tag>}
          {ticket.fu && <Tag color="blue">FOLLOW-UP</Tag>}
          {ticket.def === 1 && <Tag color="default">DEFERRED</Tag>}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Select
          style={{ width: 100 }}
          placeholder="Priority"
          allowClear
          value={priorityFilter || undefined}
          onChange={(val) => setPriorityFilter(val ?? '')}
          options={['P1', 'P2', 'P3', 'P4', 'P5'].map((pri) => ({
            value: pri,
            label: pri,
          }))}
        />
        <Select
          style={{ width: 140 }}
          placeholder="Flag"
          allowClear
          value={flagFilter || undefined}
          onChange={(val) => setFlagFilter(val ?? '')}
          options={[
            { value: 'br', label: 'Past SLA' },
            { value: 'r24', label: 'At Risk 24h' },
            { value: 'un', label: 'Unowned' },
            { value: 'pr', label: 'Proactive' },
            { value: 'def', label: 'Deferred' },
          ]}
        />
        <Select
          mode="multiple"
          style={{ width: 160 }}
          placeholder="Customer"
          allowClear
          value={selectedCustomers}
          onChange={setSelectedCustomers}
          options={customerOptions}
          showSearch
          filterOption={filterOption}
          maxTagCount={1}
        />
        <Select
          mode="multiple"
          style={{ width: 150 }}
          placeholder="Building"
          allowClear
          value={selectedBuildings}
          onChange={setSelectedBuildings}
          options={buildingOptions}
          showSearch
          filterOption={filterOption}
          maxTagCount={1}
        />
        <Select
          mode="multiple"
          style={{ width: 150 }}
          placeholder="Category"
          allowClear
          value={selectedCategories}
          onChange={setSelectedCategories}
          options={categoryOptions}
          showSearch
          filterOption={filterOption}
          maxTagCount={1}
        />
        <Select
          mode="multiple"
          style={{ width: 140 }}
          placeholder="Device"
          allowClear
          value={selectedDevices}
          onChange={setSelectedDevices}
          options={deviceOptions}
          showSearch
          filterOption={filterOption}
          maxTagCount={1}
        />
        <Select
          mode="multiple"
          style={{ width: 150 }}
          placeholder="Owner"
          allowClear
          value={selectedOwners}
          onChange={setSelectedOwners}
          options={ownerOptions}
          showSearch
          filterOption={filterOption}
          maxTagCount={1}
        />
        <Select
          mode="multiple"
          style={{ width: 150 }}
          placeholder="Age status"
          allowClear
          value={selectedAgeStatuses}
          onChange={setSelectedAgeStatuses}
          options={AGE_STATUS_OPTIONS}
          maxTagCount={1}
        />
        <Input
          style={{ width: 220 }}
          placeholder="Search tickets..."
          value={search}
          onChange={(evt) => setSearch(evt.target.value)}
          allowClear
        />
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748B' }}>
          {filtered.length} of {tickets.length} tickets
        </span>
      </div>

      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          onShowSizeChange: (_current, size) => setPageSize(size),
        }}
      />
    </div>
  );
};

export default FindingsTab;
